import { useMutation } from '@apollo/client';
import { useState, useCallback } from 'react';
import { completeInspectionInstanceMutation } from 'graphql-queries';

export interface CompleteInspectionInstanceInput {
  channelId: string;
  instanceId: string;
  completedAt: Date;
  notes?: string;
}

type CompleteInspectionInstanceMutationResult = {
  completeInspectionInstance: {
    success: boolean;
    newVersion: number;
  } | null;
};

type CompleteInspectionInstanceMutationVariables = {
  instanceId: string;
  channelId: string;
  completedAt: string;
  notes?: string;
};
export interface UseCompleteInspectionInstanceResult {
  completeInstance: (
    input: CompleteInspectionInstanceInput
  ) => Promise<{ success: boolean; newVersion: number }>;
  loading: boolean;
  error: Error | undefined;
}

export function useCompleteInspectionInstance(): UseCompleteInspectionInstanceResult {
  const [mutate] = useMutation<
    CompleteInspectionInstanceMutationResult,
    CompleteInspectionInstanceMutationVariables
  >(completeInspectionInstanceMutation);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | undefined>();

  const completeInstance = useCallback(
    async (input: CompleteInspectionInstanceInput) => {
      setLoading(true);
      setError(undefined);

      try {
        const result = await mutate({
          variables: {
            instanceId: input.instanceId,
            channelId: input.channelId,
            completedAt: input.completedAt.toISOString(),
            notes: input.notes,
          },
        });

        if (!result.data?.completeInspectionInstance) {
          throw new Error('Failed to complete inspection instance');
        }

        return {
          success: result.data.completeInspectionInstance.success,
          newVersion: result.data.completeInspectionInstance.newVersion,
        };
      } catch (err) {
        const wrappedError =
          err instanceof Error ? err : new Error('Unknown error occurred');
        setError(wrappedError);
        throw wrappedError;
      } finally {
        setLoading(false);
      }
    },
    [mutate]
  );

  return {
    completeInstance,
    loading,
    error,
  };
}
